/**
 * The run page's forecast: what is still to come on a run's critical path, when it should
 * finish and how late it could, from the loop's learned step durations.
 *
 * @since 1.0.0
 */
import { Schema } from "effect";
import type { AgentRow, ConditionAttempt, ConditionRow, PartRow, RunView } from "./RunView.ts";

/**
 * Typical duration of one kind of step, from past runs (or a default).
 *
 * @since 1.0.0
 */
export const DurationStat = Schema.Struct({ p50Ms: Schema.Number, p90Ms: Schema.Number, samples: Schema.Number });
export type DurationStat = typeof DurationStat.Type;

/**
 * A loop's learned step durations. Step keys:
 * "setup" · "build" (a subagent's first build) · "fix" (a follow-up subagent) ·
 * "part:<name>" (one repo-check part, e.g. "part:lint") and "part" (any part without its own key) ·
 * "merge" (the no-conflicts trial merge) · "review:<conditionKind>" (e.g. "review:review.code") and
 * "review" (any review without its own key) · "pr.open" · "ci" · "mergeable".
 *
 * @since 1.0.0
 */
export const DurationsProfile = Schema.Struct({
  runs: Schema.Number,
  steps: Schema.Record({ key: Schema.String, value: DurationStat }),
});
export type DurationsProfile = typeof DurationsProfile.Type;

/**
 * Below this many finished runs the forecast is labelled "rough".
 *
 * @since 1.0.0
 */
export const PROFILE_MIN_RUNS = 3;

const MINUTE_MS = 60_000;

/** A default step: its p50 and p90 in minutes, from no samples. */
const stat = (p50Min: number, p90Min: number): DurationStat => ({
  p50Ms: p50Min * MINUTE_MS,
  p90Ms: p90Min * MINUTE_MS,
  samples: 0,
});

/**
 * Defaults when a loop has no history (minutes → ms).
 *
 * @since 1.0.0
 */
export const DEFAULT_PROFILE: DurationsProfile = {
  runs: 0,
  steps: {
    setup: stat(3, 5),
    build: stat(20, 30),
    fix: stat(8, 15),
    part: stat(6, 12),
    merge: stat(0.2, 0.5),
    review: stat(3, 5),
    "pr.open": stat(0.5, 1),
    ci: stat(14, 20),
    mergeable: stat(0.3, 1),
  },
};

/**
 * One step still to come, on the critical path. Times are ms from the run's origin (like
 * `RunView` times).
 *
 * @since 1.0.0
 */
export interface ForecastStep {
  readonly label: string;
  /** The Layers row it belongs on: a `RunRow.key`, or "agent:next" for work not started yet. */
  readonly rowKey: string;
  readonly kind: "work" | "check";
  readonly startMs: number;
  readonly endMs: number;
  readonly worstStartMs: number;
  readonly worstEndMs: number;
}

/**
 * @since 1.0.0
 */
export interface Forecast {
  readonly steps: ReadonlyArray<ForecastStep>;
  /** Expected finish (p50 path), ms from origin; absent when finished or unknown. */
  readonly etaMs?: number;
  /** Worst case (p90 path), ms from origin. */
  readonly worstMs?: number;
  /** The loop's budget in ms (definition.budget.minutes), when known. */
  readonly budgetMs?: number;
  /** The loop's budget in dollars (definition.budget.usd), when known. */
  readonly budgetUsd?: number;
  /** True while the profile has fewer than {@link PROFILE_MIN_RUNS} runs. */
  readonly rough: boolean;
}

/**
 * @since 1.0.0
 */
export interface ForecastInput {
  readonly view: RunView;
  readonly profile: DurationsProfile;
  /** ms from origin — the view's `elapsedMs`. */
  readonly nowMs: number;
  /** The loop's budget in ms, when known. */
  readonly budgetMs?: number;
  /** The loop's budget in dollars, when known. */
  readonly budgetUsd?: number;
}

/** A check's parts assumed when neither the view nor the profile names any. */
const ASSUMED_PARTS = 3;

/** How short a started step's remainder can get (p50, p90): its end is always ahead of now. */
const AGENT_FLOOR = { d50: 1 * MINUTE_MS, d90: 2 * MINUTE_MS };
const CHECK_FLOOR = { d50: 0.5 * MINUTE_MS, d90: 1 * MINUTE_MS };

/** A step's length on the p50 and the p90 path. */
interface Span {
  readonly d50: number;
  readonly d90: number;
}

/** A step before it is placed on the cursors. */
interface Planned extends Span {
  readonly label: string;
  readonly rowKey: string;
  readonly kind: ForecastStep["kind"];
}

/** Steps that start together; groups run one after another. */
type Group = ReadonlyArray<Planned>;

/** What the forecast reads off the view, once. */
interface RunState {
  readonly profile: DurationsProfile;
  readonly nowMs: number;
  readonly conditions: ReadonlyArray<ConditionRow>;
  readonly parts: ReadonlyArray<PartRow>;
  readonly running: ReadonlyArray<AgentRow>;
  /** The change branch head (8 chars) after the latest landing, if any work landed. */
  readonly head: string | undefined;
  /** The first check result's time, if any. */
  readonly firstResultMs: number | undefined;
  /** The work since the head's checks (or no head at all): every condition is due again. */
  readonly fresh: boolean;
  readonly fixPending: boolean;
}

const durationOf = (profile: DurationsProfile, key: string, generic: string): DurationStat =>
  profile.steps[key] ?? profile.steps[generic] ?? DEFAULT_PROFILE.steps[generic];

const full = (stat: DurationStat): Span => ({ d50: stat.p50Ms, d90: stat.p90Ms });

/** A step's remaining length: all of it when not started, else what is left, never under `floor`. */
const remaining = (stat: DurationStat, nowMs: number, startMs: number | undefined, floor: Span): Span =>
  startMs === undefined ? full(stat) : {
    d50: Math.max(floor.d50, stat.p50Ms - (nowMs - startMs)),
    d90: Math.max(floor.d90, stat.p90Ms - (nowMs - startMs)),
  };

const shortSha = (sha: string): string => sha.slice(0, 8);

/** The sha the latest finished, landed agent left the change branch at. */
const headOf = (agents: ReadonlyArray<AgentRow>): string | undefined => {
  const latest = agents
    .flatMap(({ detail }) =>
      detail.endedAtMs !== undefined && detail.landing?.landed === true && detail.landing.sha !== undefined
        ? [{ endedAtMs: detail.endedAtMs, sha: detail.landing.sha }]
        : []
    )
    .reduce<{ readonly endedAtMs: number; readonly sha: string } | undefined>(
      (latest, landing) => latest === undefined || landing.endedAtMs > latest.endedAtMs ? landing : latest,
      undefined,
    );
  return latest === undefined ? undefined : shortSha(latest.sha);
};

const onHead = (head: string | undefined) => (attempt: ConditionAttempt): boolean => shortSha(attempt.subject) === head;

/** The condition's last verdict, when its last attempt is on the head. */
const lastOnHead = (condition: ConditionRow, head: string | undefined): ConditionAttempt["state"] | undefined => {
  const last = condition.detail.history.at(-1);
  return last !== undefined && onHead(head)(last) ? last.state : undefined;
};

const stateOf = (view: RunView, profile: DurationsProfile, nowMs: number): RunState => {
  const conditions = view.rows.filter((row): row is ConditionRow => row.kind === "condition");
  const agents = view.rows.filter((row): row is AgentRow => row.kind === "agent");
  const running = agents.filter((agent) => agent.detail.endedAtMs === undefined);
  const head = headOf(agents);
  const results = conditions.flatMap((condition) => condition.detail.history.map((attempt) => attempt.atMs));
  const fixPending = running.length === 0 &&
    conditions.some((condition) => lastOnHead(condition, head) === "unmet");
  return {
    profile,
    nowMs,
    conditions,
    parts: view.rows.filter((row): row is PartRow => row.kind === "part"),
    running,
    head,
    firstResultMs: results.length === 0 ? undefined : Math.min(...results),
    fresh: running.length > 0 || fixPending || head === undefined,
    fixPending,
  };
};

/** Work started at `atMs` is a build until the first check result, a fix after it. */
const workKey = (state: RunState, atMs: number): "build" | "fix" =>
  state.firstResultMs === undefined || atMs < state.firstResultMs ? "build" : "fix";

const need = (state: RunState, condition: ConditionRow): boolean =>
  state.fresh || lastOnHead(condition, state.head) !== "met";

/** When the condition's check of the head started, if it has. */
const since = (state: RunState, condition: ConditionRow): number | undefined => {
  const starts = [
    ...condition.bars.filter((bar) => bar.endMs === undefined).map((bar) => bar.startMs),
    ...condition.detail.history.filter(onHead(state.head)).map((attempt) => attempt.atMs - attempt.ms),
  ];
  return state.fresh || starts.length === 0 ? undefined : Math.min(...starts);
};

const workGroup = (state: RunState): Group => {
  if (state.running.length > 0) {
    return state.running.map((agent): Planned => {
      const key = workKey(state, agent.detail.startedAtMs);
      return {
        label: agent.label,
        rowKey: agent.key,
        kind: "work",
        ...remaining(durationOf(state.profile, key, key), state.nowMs, agent.detail.startedAtMs, AGENT_FLOOR),
      };
    });
  }
  if (!state.fixPending && state.head !== undefined) return [];
  const key = workKey(state, state.nowMs);
  return [{
    label: key === "build" ? "Build agent" : "Fix agent",
    rowKey: "agent:next",
    kind: "work",
    ...full(durationOf(state.profile, key, key)),
  }];
};

/** What is left of each of a path's sequential lengths `ds` after `elapsed`, the total never under `floor`. */
const consume = (ds: ReadonlyArray<number>, elapsed: number, floor: number): ReadonlyArray<number> => {
  const left = ds.reduce<{ readonly left: number; readonly rs: ReadonlyArray<number> }>(
    (acc, d) => ({ left: Math.max(0, acc.left - d), rs: [...acc.rs, Math.max(0, d - acc.left)] }),
    { left: elapsed, rs: [] },
  ).rs;
  const short = Math.max(0, floor - left.reduce((sum, r) => sum + r, 0));
  return left.map((r, i) => i === left.length - 1 ? r + short : r);
};

/** A repo check's parts, one after another, less what its started check has done. */
const partGroups = (state: RunState, condition: ConditionRow): ReadonlyArray<Group> => {
  const rows = state.parts.filter((part) => part.conditionId === condition.detail.conditionId);
  const known = Object.entries(state.profile.steps).filter(([key]) => key.startsWith("part:"));
  const generic = durationOf(state.profile, "part", "part");
  const whole: DurationStat = known.length === 0
    ? { p50Ms: ASSUMED_PARTS * generic.p50Ms, p90Ms: ASSUMED_PARTS * generic.p90Ms, samples: 0 }
    : {
      p50Ms: known.reduce((sum, [, stat]) => sum + stat.p50Ms, 0),
      p90Ms: known.reduce((sum, [, stat]) => sum + stat.p90Ms, 0),
      samples: 0,
    };
  const units = rows.length === 0
    ? [{ label: condition.label, rowKey: condition.key, span: full(whole) }]
    : rows.map((part) => ({
      label: part.part,
      rowKey: part.key,
      span: full(durationOf(state.profile, `part:${part.part}`, "part")),
    }));
  const startMs = since(state, condition);
  const spans = startMs === undefined
    ? units.map((unit) => unit.span)
    : ((r50, r90) => r50.map((d50, i): Span => ({ d50, d90: r90[i]! })))(
      consume(units.map((unit) => unit.span.d50), state.nowMs - startMs, CHECK_FLOOR.d50),
      consume(units.map((unit) => unit.span.d90), state.nowMs - startMs, CHECK_FLOOR.d90),
    );
  return units.flatMap((unit, i): ReadonlyArray<Group> => {
    const span = spans[i]!;
    return span.d50 === 0 && span.d90 === 0
      ? []
      : [[{ label: unit.label, rowKey: unit.rowKey, kind: "check", ...span }]];
  });
};

/** One check on its condition's row, less what has run of it. */
const checkStep = (state: RunState, condition: ConditionRow, key: string, generic: string): Planned => ({
  label: condition.label,
  rowKey: condition.key,
  kind: "check",
  ...remaining(durationOf(state.profile, key, generic), state.nowMs, since(state, condition), CHECK_FLOOR),
});

/** CI, after opening the change request when no condition of the run opens it and CI has not started. */
const ciGroups = (state: RunState, condition: ConditionRow, opens: boolean): ReadonlyArray<Group> => [
  ...(opens || since(state, condition) !== undefined ? [] : [[{
    label: "Open the change request",
    rowKey: condition.key,
    kind: "check" as const,
    ...full(durationOf(state.profile, "pr.open", "pr.open")),
  }]]),
  [checkStep(state, condition, "ci", "ci")],
];

/** The critical path's groups, in order. */
const plan = (state: RunState): ReadonlyArray<Group> => {
  const due = state.conditions.filter((condition) => need(state, condition));
  const ofKind = (kind: string) => due.filter((condition) => condition.detail.kind === kind);
  const opens = state.conditions.some((condition) => condition.detail.kind === "change_request.open");
  return [
    workGroup(state),
    ...ofKind("workspace.checks").flatMap((condition) => partGroups(state, condition)),
    ...ofKind("workspace.no_conflicts").map((condition) => [checkStep(state, condition, "merge", "merge")]),
    due
      .filter((condition) => condition.detail.kind.startsWith("review."))
      .map((condition) => checkStep(state, condition, `review:${condition.detail.kind}`, "review")),
    ...ofKind("change_request.open").map((condition) => [checkStep(state, condition, "pr.open", "pr.open")]),
    ...ofKind("change_request.ci_green").flatMap((condition) => ciGroups(state, condition, opens)),
    ...ofKind("change_request.mergeable").map((condition) => [
      checkStep(state, condition, "mergeable", "mergeable"),
    ]),
  ];
};

interface Cursors {
  /** The p50 path's cursor. */
  readonly cur: number;
  /** The p90 path's cursor. */
  readonly cur90: number;
  readonly steps: ReadonlyArray<ForecastStep>;
}

/** Starts every step of `group` at the cursors and advances them by its longest step. */
const place = (at: Cursors, group: Group): Cursors => ({
  cur: at.cur + Math.max(...group.map((step) => step.d50)),
  cur90: at.cur90 + Math.max(...group.map((step) => step.d90)),
  steps: [
    ...at.steps,
    ...group.map(({ d50, d90, kind, label, rowKey }): ForecastStep => ({
      label,
      rowKey,
      kind,
      startMs: at.cur,
      endMs: at.cur + d50,
      worstStartMs: at.cur90,
      worstEndMs: at.cur90 + d90,
    })),
  ],
});

/**
 * The remaining critical path from `nowMs`: the work in hand (or the next build or fix agent),
 * then each condition still due on the change branch's head — the repo check's parts, the trial
 * merge, the reviews side by side, opening the change request, CI, mergeability — each on its
 * Layers row, with its p50 and p90 timings from the profile. A step already started keeps only
 * what is left of it. `etaMs` / `worstMs` are the p50 / p90 path's end; a finished run has
 * neither.
 *
 * @since 1.0.0
 */
export const forecastRun = (input: ForecastInput): Forecast => {
  const base = {
    rough: input.profile.runs < PROFILE_MIN_RUNS,
    ...(input.budgetMs === undefined ? {} : { budgetMs: input.budgetMs }),
    ...(input.budgetUsd === undefined ? {} : { budgetUsd: input.budgetUsd }),
  };
  if (!input.view.live) return { steps: [], ...base };
  const path = plan(stateOf(input.view, input.profile, input.nowMs))
    .filter((group) => group.length > 0)
    .reduce(place, { cur: input.nowMs, cur90: input.nowMs, steps: [] });
  return { steps: path.steps, etaMs: path.cur, worstMs: path.cur90, ...base };
};

/**
 * The forecast for a set of Layers rows (a check plus its part rows, or one agent): the latest
 * p50 and p90 end among the steps whose `rowKey` is in `rowKeys`, or undefined when none is.
 *
 * @since 1.0.0
 */
export const rowForecast = (
  forecast: Forecast,
  rowKeys: ReadonlyArray<string>,
): { readonly etaMs: number; readonly worstMs: number } | undefined => {
  const steps = forecast.steps.filter((step) => rowKeys.includes(step.rowKey));
  return steps.length === 0
    ? undefined
    : {
      etaMs: Math.max(...steps.map((step) => step.endMs)),
      worstMs: Math.max(...steps.map((step) => step.worstEndMs)),
    };
};
